// @ts-check
/**
 * Reconcile a physical stock count against system stock, tied to a sales event. Search or scroll
 * to a piece, type what you counted; a live variance shows next to it. Only rows you actually
 * typed a count for are submitted, and only ones that differ from system stock get adjusted.
 */
import { AlertTriangle, Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminInventoryService from '../services/adminInventoryService';
import adminSalesEventService from '../services/adminSalesEventService';
import { buildCountPayload, countedRows, variance } from '../utils/stockCount';

const input = 'w-full px-3 py-2 text-base border border-input rounded-lg';

const StockCountTab = () => {
  const toast = useToast();
  const [events, setEvents] = useState([]);
  const [salesEventId, setSalesEventId] = useState('');
  const [notes, setNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState(/** @type {Record<string, string>} */ ({}));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(/** @type {{counted: number, variances: any[]} | null} */ (null));

  useEffect(() => {
    adminSalesEventService
      .list()
      .then(setEvents)
      .catch((err) => toast.error(err.message || 'Failed to load events'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { items: variants } = await adminInventoryService.getAllVariants({ search: searchQuery, limit: 200 });
      setItems(variants);
    } catch (err) {
      toast.error(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, toast]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const setCount = (variantId, value) => setCounts((prev) => ({ ...prev, [variantId]: value }));

  const counted = countedRows(items, counts);
  const variancesTyped = counted.filter((row) => variance(row.stock_quantity, counts[row.variant_id]) !== 0);

  const handleSubmit = async () => {
    if (!salesEventId) {
      toast.error('Choose the sales event this count belongs to');
      return;
    }
    if (counted.length === 0) {
      toast.error('Count at least one piece first');
      return;
    }
    setSubmitting(true);
    try {
      const summary = await adminInventoryService.recordStockCount(
        buildCountPayload({ rows: items, counts, salesEventId, notes }),
      );
      setResult(summary);
      setCounts({});
      toast.success(
        summary.variances.length > 0
          ? `Count recorded - ${summary.variances.length} variance(s) adjusted`
          : 'Count recorded - everything matched',
      );
    } catch (err) {
      toast.error(err.message || 'Failed to record the count');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">Sales event *</label>
          <select className={input} value={salesEventId} onChange={(e) => setSalesEventId(e.target.value)}>
            <option value="">Choose the event...</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Notes</label>
          <input
            className={input}
            placeholder="e.g. Post-event recount"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className="relative max-w-md mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70" size={18} />
        <input
          type="text"
          placeholder="Search by product name or SKU..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
        />
      </div>

      <div className="flex items-center justify-between mb-4 bg-card border border-border rounded-lg px-4 py-3">
        <span className="text-sm text-foreground/80">
          <strong>{counted.length}</strong> counted, <strong>{variancesTyped.length}</strong> variance(s)
        </span>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50"
        >
          {submitting ? 'Saving...' : 'Save count'}
        </button>
      </div>

      {result && (
        <div className="mb-4 bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-900 rounded-lg p-4 text-sm">
          <p className="font-medium text-green-800 dark:text-green-300 mb-1">
            Last count: {result.counted} counted, {result.variances.length} variance(s) adjusted
          </p>
          {result.variances.map((v) => (
            <p key={v.variant_id} className="text-green-700 dark:text-green-300">
              {v.old_quantity} &rarr; {v.new_quantity} ({v.adjustment > 0 ? '+' : ''}
              {v.adjustment})
            </p>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    System stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Counted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Variance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {items.map((item) => {
                  const v = variance(item.stock_quantity, counts[item.variant_id]);
                  return (
                    <tr key={item.variant_id} className="hover:bg-muted/50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-foreground">{item.product_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {[item.size, item.color].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{item.sku || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-foreground">{item.stock_quantity}</td>
                      <td className="px-6 py-4">
                        <input
                          type="number"
                          min="0"
                          aria-label={`Counted quantity for ${item.product_name} ${item.size || ''}`}
                          value={counts[item.variant_id] ?? ''}
                          placeholder="-"
                          onChange={(e) => setCount(item.variant_id, e.target.value)}
                          className="w-20 px-2 py-1 border border-input rounded-lg text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {v !== null && v !== 0 && (
                          <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                            <AlertTriangle size={13} /> {v > 0 ? '+' : ''}
                            {v}
                          </span>
                        )}
                        {v === 0 && <span className="text-green-600 dark:text-green-400">match</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockCountTab;
